import { GridColumn } from '@entity-framework/entity-grid/state/entity-grid-column';
import { columnFormatters } from '@utils/display-formatters';
import { CellClassParams } from '@ag-grid-community/core';
import { EnrollmentRecordError } from './state/pension-enrollment-record.model';

const hasErrors = (params: CellClassParams): boolean => {
  return params.data?.errors && params.data.errors.length > 0;
};

const getRowErrorClassRules = () => ({
  'error-row-cell': (params: CellClassParams) => hasErrors(params)
});

export const pensionEnrollmentGridColumns = (): GridColumn[] => [
  {
    propertyName: 'employeeId',
    headerLabel: 'Employee ID',
    propertyType: 'text',
    sortable: true,
    colDefOverrides: {
      flex: 1,
      minWidth: 120,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'employeeName',
    headerLabel: 'Employee name',
    propertyType: 'text',
    sortable: true,
    colDefOverrides: {
      flex: 1.5,
      minWidth: 150,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'errors',
    headerLabel: 'Error description',
    propertyType: 'text',
    sortable: false,
    formatForDisplay: (value: EnrollmentRecordError[]) =>
      value && value.length > 0 ? value.map((e) => `• ${e.errorDescription}`).join('\n') : '-',
    colDefOverrides: {
      flex: 2,
      minWidth: 200,
      wrapText: true,
      autoHeight: true,
      cellClassRules: {
        'error-row-cell': (params: CellClassParams) => hasErrors(params),
        'error-text': (params: CellClassParams) => hasErrors(params)
      },
      cellStyle: { whiteSpace: 'pre-wrap', lineHeight: '1.4' }
    }
  },
  {
    propertyName: 'dateOfBirth',
    headerLabel: 'Date of birth',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string) => columnFormatters.date(value),
    colDefOverrides: {
      flex: 1,
      minWidth: 120,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'niNumber',
    headerLabel: 'NI number',
    propertyType: 'text',
    sortable: true,
    colDefOverrides: {
      flex: 1,
      minWidth: 120,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'addressLine1',
    headerLabel: 'Address line 1',
    propertyType: 'text',
    sortable: true,
    colDefOverrides: {
      flex: 1.2,
      minWidth: 130,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'addressLine2',
    headerLabel: 'Address line 2',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string | null) => value || '-',
    colDefOverrides: {
      flex: 1.2,
      minWidth: 130,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'addressLine3',
    headerLabel: 'Address line 3',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string | null) => value || '-',
    colDefOverrides: {
      flex: 1.2,
      minWidth: 130,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'postcode',
    headerLabel: 'Post code',
    propertyType: 'text',
    sortable: true,
    colDefOverrides: {
      flex: 1,
      minWidth: 110,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'country',
    headerLabel: 'Country',
    propertyType: 'text',
    sortable: true,
    colDefOverrides: {
      flex: 1.2,
      minWidth: 130,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'startDate',
    headerLabel: 'Start date',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string) => columnFormatters.date(value),
    colDefOverrides: {
      flex: 1,
      minWidth: 120,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'gender',
    headerLabel: 'Gender',
    propertyType: 'text',
    sortable: true,
    colDefOverrides: {
      flex: 0.8,
      minWidth: 100,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'pensionGroup',
    headerLabel: 'Pension group',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string | null) => value || '-',
    colDefOverrides: {
      flex: 1.2,
      minWidth: 130,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'paymentSource',
    headerLabel: 'Payment source',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string | null) => value || '-',
    colDefOverrides: {
      flex: 1.2,
      minWidth: 130,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'enrolmentDate',
    headerLabel: 'Enrolment date',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string) => columnFormatters.date(value),
    colDefOverrides: {
      flex: 1,
      minWidth: 120,
      cellClassRules: getRowErrorClassRules()
    }
  },
  {
    propertyName: 'workerStatusCategory',
    headerLabel: 'Worker status category',
    propertyType: 'text',
    sortable: true,
    formatForDisplay: (value: string | null) => value || '-',
    colDefOverrides: {
      flex: 1.5,
      minWidth: 160,
      cellClassRules: getRowErrorClassRules()
    }
  }
];
